import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";
import { getCurrencyFromCountry } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateFieldName } from "../../hooks/useRateFieldName";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isMax, isMin, isRequired, isZero } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface ReceiptEligibleAmountFieldProps {
  isJustChecked?: boolean;
  toggleRateOption?: boolean;
}

export const ReceiptEligibleAmountField: FC<ReceiptEligibleAmountFieldProps> = ({
  isJustChecked,
  toggleRateOption,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organization } = useCurrentUserContext();
  const { control, unregister } = useFormContext<StepTicketsFormData>();

  const fieldName = useRateFieldName("receiptEligibleAmount");
  const error = useRateFieldError("receiptEligibleAmount");

  const inputRef = useRef<HTMLDivElement | null>(null);

  const rateAmount = useWatch({ control, name: useRateFieldName("amount") }) as number;

  useEffect(() => {
    if (isJustChecked && toggleRateOption && inputRef.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJustChecked]);

  useEffect(() => {
    if (error && toggleRateOption && inputRef.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, toggleRateOption]);

  useEffect(() => {
    return () => {
      unregister(fieldName, { keepValue: true });
    };
  }, [fieldName, unregister]);

  return (
    <Box sx={{ display: "inline-block", width: 90 }}>
      <DashboardAmountInput
        name={fieldName}
        control={control}
        locale={locale}
        currency={getCurrencyFromCountry(organization?.country)}
        error={Boolean(error?.message)}
        rules={[
          isRequired(t("common", "isRequired")),
          isMin(100, t("dashboard", "ticketing.rateEligibleAmountMin")),
          isMax(rateAmount, t("dashboard", "ticketing.rateEligibleAmountMax")),
          isZero(t("dashboard", "ticketing.rateEligibleAmountMin")),
          ...(!organization?.canGenerateReceipt
            ? [
                {
                  validate: {
                    receiptNotAvailable: () =>
                      t("dashboard", "ticketing.receiptNotAvailableRemoveEligibleAmount") || false,
                  },
                },
              ]
            : []),
        ]}
        inputRef={inputRef}
        size="small"
        onClick={(e) => e.stopPropagation()}
      />
    </Box>
  );
};
